/**
 * Optimized Impersonation Service
 * 
 * Enhanced impersonation management with reduced complexity, better performance,
 * and enterprise-grade features following the simplified services pattern.
 */

import type { User, Organization } from '@/lib/types';
import { BaseService, type ServiceConfig } from './baseService';
import { organizationManager } from './organizationManager';
import { userManager } from './userManager';

// =============================================================================
// TYPES AND INTERFACES
// =============================================================================

export interface ImpersonationState {
  isImpersonating: boolean;
  originalUser: User | null;
  impersonatedOrganization: Organization | null;
  impersonatedUser: User | null;
  startTime: number | null;
  sessionId: string | null;
}

export interface ImpersonationConfig extends ServiceConfig {
  maxSessionDuration: number; // Maximum impersonation session in milliseconds
  auditLogging: boolean;
  persistSession: boolean;
  autoExpire: boolean;
}

export interface ImpersonationResult {
  success: boolean;
  state: ImpersonationState;
  sessionId?: string;
  error?: string;
}

export interface ImpersonationAuditEvent {
  eventType: 'IMPERSONATION_STARTED' | 'IMPERSONATION_STOPPED' | 'IMPERSONATION_EXPIRED';
  originalUserId: string;
  targetOrganizationId: string;
  targetUserId?: string;
  sessionId: string;
  timestamp: number;
  metadata?: Record<string, unknown>;
}

// =============================================================================
// IMPERSONATION VALIDATION STRATEGIES
// =============================================================================

class ImpersonationValidator {
  /**
   * Validate if user can start impersonation
   */
  static canImpersonate(user: User): { valid: boolean; reason?: string } {
    if (!user.roles?.includes('Super Admin')) {
      return { valid: false, reason: 'Only Super Admins can impersonate organizations' };
    }

    if (user.status !== 'Active') {
      return { valid: false, reason: 'User account is not active' };
    }

    return { valid: true };
  }

  /**
   * Validate organization access
   */
  static async canAccessOrganization(
    organizationId: string
  ): Promise<{ valid: boolean; organization?: Organization; reason?: string }> {
    try {
      const organization = await organizationManager.getOrganizationById(organizationId);
      
      if (!organization) {
        return { valid: false, reason: 'Organization not found' };
      }

      if (organization.status !== 'Active') {
        return { valid: false, reason: 'Organization is not active' };
      }

             return { valid: true, organization };
     } catch {
       return { valid: false, reason: 'Failed to validate organization access' };
     }
  }

  /**
   * Validate target admin user
   */
  static async canImpersonateUser(
    userId: string,
    organizationId: string
  ): Promise<{ valid: boolean; user?: User; reason?: string }> {
    try {
      const user = await userManager.getUserById(userId);
      
      if (!user) {
        return { valid: false, reason: 'Target user not found' };
      }

      if (user.organizationId !== organizationId) {
        return { valid: false, reason: 'User does not belong to the target organization' };
      }

      if (!user.roles?.includes('Admin')) {
        return { valid: false, reason: 'Target user is not an admin' };
      }

      if (user.status !== 'Active') {
        return { valid: false, reason: 'Target user account is not active' };
      }

             return { valid: true, user };
     } catch {
       return { valid: false, reason: 'Failed to validate target user' };
     }
  }
}

// =============================================================================
// IMPERSONATION SESSION MANAGER
// =============================================================================

class ImpersonationSessionManager {
  private static readonly SESSION_STORAGE_KEY = 'courtly_impersonation_session';

  /**
   * Generate unique session ID
   */
  static generateSessionId(): string {
    return `imp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Save session to storage if persistence is enabled
   */
  static saveSession(state: ImpersonationState, persistSession: boolean): void {
    if (!persistSession || typeof window === 'undefined') return;

    try {
      sessionStorage.setItem(
        ImpersonationSessionManager.SESSION_STORAGE_KEY,
        JSON.stringify(state)
      );
    } catch (error) {
      console.warn('Failed to persist impersonation session:', error);
    }
  }

  /**
   * Load session from storage
   */
  static loadSession(): ImpersonationState | null {
    if (typeof window === 'undefined') return null;

    try {
      const stored = sessionStorage.getItem(ImpersonationSessionManager.SESSION_STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.warn('Failed to load impersonation session:', error);
      return null;
    }
  }

  /**
   * Clear session from storage
   */
  static clearSession(): void {
    if (typeof window === 'undefined') return;

    try {
      sessionStorage.removeItem(ImpersonationSessionManager.SESSION_STORAGE_KEY);
    } catch (error) {
      console.warn('Failed to clear impersonation session:', error);
    }
  }

  /**
   * Check if session is expired
   */
  static isSessionExpired(state: ImpersonationState, maxDuration: number): boolean {
    if (!state.startTime) return false;
    return Date.now() - state.startTime > maxDuration;
  }
}

// =============================================================================
// OPTIMIZED IMPERSONATION SERVICE
// =============================================================================

export class ImpersonationService extends BaseService {
  private static instance: ImpersonationService;
  
  private readonly DEFAULT_IMPERSONATION_CONFIG: Required<ImpersonationConfig> = {
    ...this.config,
    maxSessionDuration: 8 * 60 * 60 * 1000, // 8 hours
    auditLogging: true,
    persistSession: true,
    autoExpire: true,
  };

  private impersonationConfig: Required<ImpersonationConfig>;
  private impersonationState: ImpersonationState;
  private listeners: Array<(state: ImpersonationState) => void> = [];
  private sessionExpiryTimer?: NodeJS.Timeout;

  private constructor(config: Partial<ImpersonationConfig> = {}) {
    super(config);
    this.impersonationConfig = { ...this.DEFAULT_IMPERSONATION_CONFIG, ...config };
    this.impersonationState = this.initializeState();
    this.setupSessionExpiry();
  }

  static getInstance(config?: Partial<ImpersonationConfig>): ImpersonationService {
    if (!ImpersonationService.instance) {
      ImpersonationService.instance = new ImpersonationService(config);
    }
    return ImpersonationService.instance;
  }

  // =============================================================================
  // INITIALIZATION AND STATE MANAGEMENT
  // =============================================================================

  /**
   * Initialize impersonation state
   */
  private initializeState(): ImpersonationState {
    const emptyState: ImpersonationState = {
      isImpersonating: false,
      originalUser: null,
      impersonatedOrganization: null,
      impersonatedUser: null,
      startTime: null,
      sessionId: null,
    };

    // Try to restore session if persistence is enabled
    if (this.impersonationConfig.persistSession) {
      const storedState = ImpersonationSessionManager.loadSession();
      
      if (storedState && !ImpersonationSessionManager.isSessionExpired(
        storedState, 
        this.impersonationConfig.maxSessionDuration
      )) {
        return storedState;
      }
    }

    return emptyState;
  }

  /**
   * Setup automatic session expiry
   */
  private setupSessionExpiry(): void {
    if (!this.impersonationConfig.autoExpire) return;

    if (this.impersonationState.isImpersonating && this.impersonationState.startTime) {
      const remainingTime = this.impersonationConfig.maxSessionDuration - 
        (Date.now() - this.impersonationState.startTime);

      if (remainingTime > 0) {
        this.sessionExpiryTimer = setTimeout(() => {
          this.expireSession();
        }, remainingTime);
      } else {
        // Session already expired
        this.expireSession();
      }
    }
  }

  // =============================================================================
  // CORE IMPERSONATION OPERATIONS
  // =============================================================================

  /**
   * Start impersonation with enhanced validation and error handling
   */
  async startImpersonation(
    originalUser: User,
    organizationId: string,
    adminUserId?: string
  ): Promise<ImpersonationResult> {
    try {
      // Validate permissions
      const userValidation = ImpersonationValidator.canImpersonate(originalUser);
      if (!userValidation.valid) {
        return {
          success: false,
          state: this.impersonationState,
          error: userValidation.reason,
        };
      }

      // Validate organization access
      const orgValidation = await ImpersonationValidator.canAccessOrganization(organizationId);
      if (!orgValidation.valid) {
        return {
          success: false,
          state: this.impersonationState,
          error: orgValidation.reason,
        };
      }

      const organization = orgValidation.organization!;
      let impersonatedUser: User;

      // Handle specific admin user or find suitable admin
      if (adminUserId) {
        const userValidation = await ImpersonationValidator.canImpersonateUser(
          adminUserId,
          organizationId
        );
        
        if (!userValidation.valid) {
          return {
            success: false,
            state: this.impersonationState,
            error: userValidation.reason,
          };
        }

        impersonatedUser = userValidation.user!;
      } else {
        impersonatedUser = await this.findOrCreateAdminUser(originalUser, organization);
      }

      // Create new session
      const sessionId = ImpersonationSessionManager.generateSessionId();
      const newState: ImpersonationState = {
        isImpersonating: true,
        originalUser,
        impersonatedOrganization: organization,
        impersonatedUser,
        startTime: Date.now(),
        sessionId,
      };

      // Update state and notify
      await this.updateState(newState);

      // Audit logging
      if (this.impersonationConfig.auditLogging) {
        await this.logAuditEvent({
          eventType: 'IMPERSONATION_STARTED',
          originalUserId: originalUser.uid,
          targetOrganizationId: organizationId,
          targetUserId: impersonatedUser.uid,
          sessionId,
          timestamp: Date.now(),
        });
      }

      return {
        success: true,
        state: newState,
        sessionId,
      };

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Failed to start impersonation:', error);
      
      return {
        success: false,
        state: this.impersonationState,
        error: errorMessage,
      };
    }
  }

  /**
   * Stop impersonation with cleanup
   */
  async stopImpersonation(): Promise<ImpersonationResult> {
    try {
      const currentState = this.impersonationState;
      
      // Create empty state
      const newState: ImpersonationState = {
        isImpersonating: false,
        originalUser: null,
        impersonatedOrganization: null,
        impersonatedUser: null,
        startTime: null,
        sessionId: null,
      };

      // Update state
      await this.updateState(newState);

      // Audit logging
      if (this.impersonationConfig.auditLogging && currentState.sessionId) {
        await this.logAuditEvent({
          eventType: 'IMPERSONATION_STOPPED',
          originalUserId: currentState.originalUser?.uid || '',
          targetOrganizationId: currentState.impersonatedOrganization?.id || '',
          targetUserId: currentState.impersonatedUser?.uid,
          sessionId: currentState.sessionId,
          timestamp: Date.now(),
        });
      }

      return {
        success: true,
        state: newState,
      };

    } catch (error) {
      console.error('Failed to stop impersonation:', error);
      
      return {
        success: false,
        state: this.impersonationState,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Expire session automatically
   */
  private async expireSession(): Promise<void> {
    if (!this.impersonationState.isImpersonating) return;

    const currentState = this.impersonationState;
    
    // Update state
    await this.updateState({
      isImpersonating: false,
      originalUser: null,
      impersonatedOrganization: null,
      impersonatedUser: null,
      startTime: null,
      sessionId: null,
    });

    // Audit logging
    if (this.impersonationConfig.auditLogging && currentState.sessionId) {
      await this.logAuditEvent({
        eventType: 'IMPERSONATION_EXPIRED',
        originalUserId: currentState.originalUser?.uid || '',
        targetOrganizationId: currentState.impersonatedOrganization?.id || '',
        targetUserId: currentState.impersonatedUser?.uid,
        sessionId: currentState.sessionId,
        timestamp: Date.now(),
      });
    }

    console.warn('Impersonation session expired and was automatically terminated');
  }

  // =============================================================================
  // HELPER METHODS
  // =============================================================================

  /**
   * Find or create admin user for impersonation
   */
  private async findOrCreateAdminUser(originalUser: User, organization: Organization): Promise<User> {
    // Try to find existing admin
    const allUsers = await userManager.getAllUsers();
    const orgAdmins = allUsers.filter(user => 
      user.organizationId === organization.id && 
      user.roles?.includes('Admin') &&
      user.status === 'Active'
    );

    if (orgAdmins.length > 0) {
      return orgAdmins[0];
    }

    // Fallback: Create generic admin context
    return {
      ...originalUser,
      organizationId: organization.id,
      roles: ['Admin'], // Super Admin acts as Admin when impersonating
      displayName: `${originalUser.firstName} ${originalUser.lastName} (Impersonating)`,
    };
  }

  /**
   * Update state with persistence and notifications
   */
  private async updateState(newState: ImpersonationState): Promise<void> {
    this.impersonationState = newState;

    // Clear existing timer
    if (this.sessionExpiryTimer) {
      clearTimeout(this.sessionExpiryTimer);
      this.sessionExpiryTimer = undefined;
    }

    // Setup new timer if impersonating
    if (newState.isImpersonating && this.impersonationConfig.autoExpire) {
      this.sessionExpiryTimer = setTimeout(() => {
        this.expireSession();
      }, this.impersonationConfig.maxSessionDuration);
    }

    // Persist session
    ImpersonationSessionManager.saveSession(newState, this.impersonationConfig.persistSession);

    // Clear session if not impersonating
    if (!newState.isImpersonating) {
      ImpersonationSessionManager.clearSession();
    }

    // Notify listeners
    this.notifyListeners();
  }

  /**
   * Log audit events
   */
  private async logAuditEvent(event: ImpersonationAuditEvent): Promise<void> {
    try {
      // In a real implementation, this would send to an audit service
      console.info('🔍 Impersonation Audit Event:', event);
      
      // Store in local storage for debugging/monitoring
      if (typeof window !== 'undefined') {
        const existingLogs = JSON.parse(localStorage.getItem('impersonation_audit_log') || '[]');
        existingLogs.push(event);
        
        // Keep only last 100 events
        if (existingLogs.length > 100) {
          existingLogs.splice(0, existingLogs.length - 100);
        }
        
        localStorage.setItem('impersonation_audit_log', JSON.stringify(existingLogs));
      }
    } catch (error) {
      console.error('Failed to log audit event:', error);
    }
  }

  // =============================================================================
  // PUBLIC API METHODS
  // =============================================================================

  /**
   * Get current impersonation state (immutable)
   */
  getImpersonationState(): Readonly<ImpersonationState> {
    return Object.freeze({ ...this.impersonationState });
  }

  /**
   * Get effective user with session validation
   */
  getEffectiveUser(originalUser: User): User {
    // Check session expiry
    if (this.impersonationState.isImpersonating && 
        ImpersonationSessionManager.isSessionExpired(
          this.impersonationState, 
          this.impersonationConfig.maxSessionDuration
        )) {
      this.expireSession();
      return originalUser;
    }

    return this.impersonationState.isImpersonating && this.impersonationState.impersonatedUser
      ? this.impersonationState.impersonatedUser
      : originalUser;
  }

  /**
   * Check if currently impersonating
   */
  isImpersonating(): boolean {
    return this.impersonationState.isImpersonating;
  }

  /**
   * Get impersonated organization
   */
  getImpersonatedOrganization(): Organization | null {
    return this.impersonationState.impersonatedOrganization;
  }

  /**
   * Get session statistics
   */
  getSessionStats(): {
    isActive: boolean;
    duration: number; // in milliseconds
    remainingTime: number; // in milliseconds
    sessionId: string | null;
    autoExpire: boolean;
  } {
    const duration = this.impersonationState.startTime 
      ? Date.now() - this.impersonationState.startTime 
      : 0;

    const remainingTime = this.impersonationState.startTime
      ? Math.max(0, this.impersonationConfig.maxSessionDuration - duration)
      : 0;

    return {
      isActive: this.impersonationState.isImpersonating,
      duration,
      remainingTime,
      sessionId: this.impersonationState.sessionId,
      autoExpire: this.impersonationConfig.autoExpire,
    };
  }

  /**
   * Subscribe to state changes
   */
  subscribe(listener: (state: ImpersonationState) => void): () => void {
    this.listeners.push(listener);
    
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  /**
   * Notify all listeners of state changes
   */
  private notifyListeners(): void {
    this.listeners.forEach(listener => {
      try {
        listener(this.impersonationState);
      } catch (error) {
        console.error('Error in impersonation listener:', error);
      }
    });
  }

  /**
   * Get audit log for current session
   */
  getAuditLog(): ImpersonationAuditEvent[] {
    if (typeof window === 'undefined') return [];
    
    try {
      return JSON.parse(localStorage.getItem('impersonation_audit_log') || '[]');
    } catch (error) {
      console.error('Failed to get audit log:', error);
      return [];
    }
  }

  /**
   * Cleanup method for service disposal
   */
  dispose(): void {
    if (this.sessionExpiryTimer) {
      clearTimeout(this.sessionExpiryTimer);
      this.sessionExpiryTimer = undefined;
    }

    if (this.impersonationState.isImpersonating) {
      this.stopImpersonation();
    }

    this.listeners = [];
    ImpersonationSessionManager.clearSession();
    super.dispose();
  }
}

// =============================================================================
// SINGLETON INSTANCE
// =============================================================================

export const impersonationService = ImpersonationService.getInstance();

// Cleanup on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    impersonationService.dispose();
  });
} 
